import { useEffect, useRef, useState } from 'preact/hooks';
import { clearThreadUnread, subscribeUnread, threadKey, unreadTotal, type UnreadTotal } from '../lib/unread';
import { markThreadRead } from '../lib/api';
import type { ChatLane } from '../routes/route';

// CV6b §4 — the shell's window onto lib/unread's store. Kept as a hook in
// its own file (the useBreakpoint convention) so the store itself stays a
// plain module the unit suite can drive without rendering anything.
export function useUnreadTotal(): UnreadTotal {
  const [total, setTotal] = useState<UnreadTotal>(() => unreadTotal());
  useEffect(() => {
    // Re-read on subscribe: a list read that landed between the initial
    // state and this effect would otherwise be missed for a whole render.
    setTotal(unreadTotal());
    return subscribeUnread(setTotal);
  }, []);
  return total;
}

/**
 * CV6b §4 — OPENING A THREAD MARKS IT READ.
 *
 * A HUMAN opening it, and nothing else: a push arriving marks nothing (a
 * notification is something that happened to someone, not something they
 * have seen), which is why this hook hangs off the OPEN route and not off
 * any message arriving.
 *
 * One call per thread per mount-world. It fires whether or not the loaded
 * list showed a count, because the list can be stale (a deep link from a
 * notification has no list at all yet) and the server's marker is the one
 * that survives a reload.
 *
 * `onMarked` lets the screen zero its own row without a second list read —
 * "the list and badge update without a reload" is the acceptance, and a
 * re-read would be both slower and a request nobody needs.
 */
export function useMarkThreadRead(
  tenantId: string,
  open: { lane: ChatLane; id: string } | undefined,
  onMarked?: (key: string) => void
): void {
  const marked = useRef<string | null>(null);
  const notify = useRef(onMarked);
  notify.current = onMarked;

  const key = open ? `${tenantId}|${threadKey(open.lane, open.id)}` : null;
  useEffect(() => {
    if (!open || key === null || marked.current === key) return;
    marked.current = key;
    const thread = threadKey(open.lane, open.id);
    // Optimistic locally, honest globally: the row and the badge clear the
    // moment the human opens the thread, and the SERVER is told in the same
    // breath so a reload agrees. A refusal (403 on a thread this caller may
    // not read) leaves the server's truth alone — and the thread they are
    // looking at is refusing to render anyway.
    clearThreadUnread(thread);
    notify.current?.(thread);
    void markThreadRead(tenantId, {
      lane: open.lane,
      ...(open.lane === 'job' ? { recordId: open.id } : { membershipId: open.id }),
    }).catch(() => {
      // Nothing to say to the human here: they are IN the thread, the
      // marker is a bookkeeping fact, and the next open retries it.
      marked.current = null;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
}
